/**
 * Утилита дедупликации запросов для предотвращения дублирующих API вызовов
 *
 * Использование:
 * const deduped = withDeduplication(myApiFunction)
 * const result = await deduped(params) // Дедуплицирует при быстрых повторных вызовах
 */

const inFlightRequests = new Map()

/**
 * Оборачивает async функцию для дедупликации параллельных вызовов с одинаковым ключом
 * @param {Function} fn - Async функция для обёртки
 * @param {Function} keyFn - Опциональная функция для генерации ключа кэша из аргументов
 * @returns {Function} Обёрнутая функция с дедупликацией
 */
export function withDeduplication(fn, keyFn = (...args) => JSON.stringify(args)) {
  // Используем имя функции как часть ключа, чтобы разные функции с одинаковыми аргументами не конфликтовали
  const fnKey = fn.name || Math.random().toString(36).slice(2)
  return async function (...args) {
    const key = `${fnKey}:${keyFn(...args)}`

    // Если уже есть запрос с этим ключом, возвращаем его промис
    if (inFlightRequests.has(key)) {
      return inFlightRequests.get(key)
    }

    // Создаём новый промис запроса
    const promise = fn(...args)
      .finally(() => {
        // Очищаем после завершения запроса (успех или ошибка)
        inFlightRequests.delete(key)
      })

    // Сохраняем промис
    inFlightRequests.set(key, promise)

    return promise
  }
}

/**
 * Очистка всех отслеживаемых запросов (полезно для тестов или очистки)
 */
export function clearDeduplicationCache() {
  inFlightRequests.clear()
}
